
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { PlayIcon, VolumeUpIcon, SparklesIcon, UserIcon, } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import React, { useState, useRef } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { ACTORS } from '@/constants/actors';
const TalkingVideosLanding = () => {
    const { t } = useLocale('ai-talking-videos');
    const [activeExample, setActiveExample] = useState(0);
    const [isExamplePlaying, setIsExamplePlaying] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const exampleVideoRef = useRef<HTMLVideoElement>(null);
    // Get unique categories from actors
    const categories = [
        'all',
        ...Array.from(new Set(ACTORS.map((actor) => actor.category).filter((cat): cat is string => Boolean(cat)))),
    ];
    // Filter actors by selected category
    const filteredActors = selectedCategory === 'all'
        ? ACTORS.slice(0, 18) // Show 3 rows for landing page (6 cols × 3 rows on xl screens)
        : ACTORS.filter((actor) => actor.category === selectedCategory).slice(0, 18);
    const features = [
        {
            name: '40+ Languages & Accents',
            description: 'Choose from over 40 languages and accents for truly global content creation',
            icon: '🎤',
            highlight: false,
        },
        {
            name: 'AI Image Generator Integration',
            description: 'Upload your own photo OR generate one with our built-in AI Image Generator',
            icon: SparklesIcon,
        },
        {
            name: 'Auto-Generated Captions',
            description: 'Automatically generate TikTok-style captions for maximum engagement on social media',
            icon: '📚',
        },
        {
            name: 'Multiple Voice Options',
            description: 'Choose from diverse AI voices or clone your own voice for authentic personalization',
            icon: VolumeUpIcon,
        },
        {
            name: 'Image & Video Support',
            description: 'Works with both static images and videos - bring any visual content to life',
            icon: PlayIcon,
        },
        {
            name: 'Custom Audio Upload',
            description: 'Upload your own audio recordings for perfect lip-sync with any image or video',
            icon: SparklesIcon,
        },
        {
            name: 'Fast Generation',
            description: 'Get your talking videos in minutes with our optimized AI processing',
            icon: '⚡',
        },
        {
            name: 'Professional Quality',
            description: 'Studio-grade output with natural lip movements and facial expressions',
            icon: '🎬',
        },
        {
            name: 'Commercial Usage',
            description: 'On paid plans, use generated videos for marketing, social media, and business presentations',
            icon: '💼',
        },
    ];
    const useCases = [
        {
            title: 'Marketing & Advertising',
            description: 'Create engaging product demos and personalized marketing messages',
            icon: '📢',
        },
        {
            title: 'Educational Content',
            description: 'Transform static educational materials into dynamic video lessons',
            icon: '🎓',
        },
        {
            title: 'Social Media Content',
            description: 'Generate viral-worthy talking videos for TikTok, Instagram, and YouTube',
            icon: '📱',
        },
        {
            title: 'Corporate Communications',
            description: 'Deliver company updates and training materials with a personal touch',
            icon: '🏢',
        },
        {
            title: 'Personal Branding',
            description: 'Create consistent video content without always being on camera',
            icon: '🌟',
        },
        {
            title: 'Language Learning',
            description: 'Practice pronunciation with AI avatars speaking in different languages',
            icon: '🌍',
        },
    ];
    const examples = [
        // person example
        {
            title: 'Person Speaking',
            description: 'Upload a photo of a person and make them speak',
            videoUrl: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_9c77ff75-512e-40d9-92c9-d7e7ddb8a1b8.mp4',
            prompt: 'AI-generated talking avatar with realistic lip-sync and expressions',
        },
        // marketing example
        {
            title: 'Marketing Videos',
            description: 'Generate product demos and promotional content',
            videoUrl: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_captioned_1a22a2fa-5475-453e-9607-06a0ee3486f9.mp4',
            prompt: 'Brand ambassador showcasing products naturally',
        },
        {
            title: 'AI Avatar Speaking',
            description: 'Transform any portrait into a lifelike talking avatar',
            videoUrl: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_26bc240b-dd81-4c5f-a10a-219d6dcf1b9e.mp4',
            prompt: 'Professional AI avatar delivering personalized messages',
        },
        {
            title: 'Educational Content',
            description: 'Create engaging educational videos with AI presenters',
            videoUrl: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_940d1d46-e6b7-4977-a589-e7af2a906e85.mp4',
            prompt: 'AI teacher explaining concepts with natural expressions',
        },
    ];
    const pricingFeatures = [
        'Up to 5-minute videos',
        'High-quality voice synthesis',
        'Natural lip-sync technology',
        'Over 40 languages & accents',
        'AI Image Generator integration',
        'Auto-generated TikTok-style captions',
        'Multiple voice options',
        'Custom audio upload',
        'Commercial rights on paid plans',
        'Fast processing times',
        'Priority support',
    ];
    const faqItems = [
        {
            question: 'How does AI talking video generation work?',
            answer: 'Our AI analyzes your input image or video, then synchronizes lip movements and facial expressions with your provided audio or text-to-speech voice to create realistic talking videos.',
        },
        {
            question: 'What file formats are supported?',
            answer: 'We support popular image formats (JPG, PNG, WEBP) and video formats (MP4, MOV, AVI). Audio files can be uploaded in MP3, WAV, or M4A formats.',
        },
        {
            question: 'How long does it take to generate a talking video?',
            answer: 'Most talking videos are generated within 2-5 minutes, depending on the length and complexity of your content.',
        },
        {
            question: 'Can I use my own voice?',
            answer: 'Yes! You can upload your own audio recordings or use our voice cloning feature to create a personalized AI voice.',
        },
        {
            question: 'Are there any usage rights restrictions?',
            answer: 'Videos made on a paid plan come with commercial usage rights, so you can use them for marketing, social media, and business presentations. Videos made on the Free plan are for non-commercial use only.',
        },
        {
            question: 'What languages are supported?',
            answer: 'Our AI supports multiple languages including English, Spanish, French, German, Italian, Portuguese, and many more.',
        },
    ];
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'AI Talking Video Generator',
        applicationCategory: 'Multimedia',
        operatingSystem: 'Web Browser',
        description: 'Transform any image or video into a talking avatar with AI. Perfect for marketing, education, and social media content.',
        url: 'https://easy-peasy.ai/ai-video-generator/talking',
        creator: {
            '@type': 'Organization',
            name: 'Easy-Peasy.ai',
            url: 'https://easy-peasy.ai',
        },
        featureList: [
            'AI-powered lip sync',
            'Over 40 languages and accents',
            'AI Image Generator integration',
            'Auto-generated captions',
            'Multiple voice options',
            'Custom audio upload',
            'Professional quality output',
            'Commercial rights on paid plans',
            'Fast processing',
        ],
    };
    const faqStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    };
    const breadcrumbStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://easy-peasy.ai',
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: 'AI Video Generator',
                item: 'https://easy-peasy.ai/ai-video-generator',
            },
            {
                '@type': 'ListItem',
                position: 3,
                name: 'AI Talking Video Generator',
                item: 'https://easy-peasy.ai/ai-video-generator/talking',
            },
        ],
    };
    return (<main className='relative overflow-hidden bg-white dark:bg-slate-900'>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData),
        }}/>
        <script type='application/ld+json' dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqStructuredData),
        }}/>
        <script type='application/ld+json' dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbStructuredData),
        }}/>
        <link rel='canonical' href='https://easy-peasy.ai/ai-video-generator/talking'/>
        <meta name='keywords' content='AI talking video, lip sync AI, talking avatar, AI video generator, deepfake, voice cloning, synthetic media, AI presenter, talking photos, animated portraits'/>
        <meta name='author' content='Easy-Peasy.ai'/>
        <meta name='robots' content='index, follow'/>
        <meta property='og:type' content='website'/>
        <meta property='og:site_name' content='Easy-Peasy.ai'/>
        <meta name='twitter:card' content='summary_large_image'/>
        <meta name='twitter:site' content='@EasyPeasyAI'/>
        <meta name='viewport' content='width=device-width, initial-scale=1'/>
        <meta httpEquiv='Content-Type' content='text/html; charset=utf-8'/>
      </Head>

      {/* Modern Gradient Background */}
      <div className='absolute inset-0 -z-10'>
        {/* Mesh gradient background */}
        <div className='absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-slate-900 dark:via-purple-900/20 dark:to-slate-900'/>

        {/* Animated gradient orbs */}
        <div className='absolute top-0 -left-4 h-72 w-72 animate-blob rounded-full bg-purple-300 opacity-70 mix-blend-multiply blur-xl filter dark:bg-purple-700 dark:opacity-30'/>
        <div className='animation-delay-2000 absolute top-0 -right-4 h-72 w-72 animate-blob rounded-full bg-yellow-300 opacity-70 mix-blend-multiply blur-xl filter dark:bg-yellow-700 dark:opacity-30'/>
        <div className='animation-delay-4000 absolute -bottom-8 left-20 h-72 w-72 animate-blob rounded-full bg-pink-300 opacity-70 mix-blend-multiply blur-xl filter dark:bg-pink-700 dark:opacity-30'/>

        {/* Grid pattern overlay */}
        <div className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] dark:opacity-20'/>
      </div>

      {/* Floating elements */}
      <div className='absolute inset-0 -z-10 overflow-hidden'>
        <div className='absolute left-[10%] top-[20%] h-32 w-32 animate-float rounded-full bg-gradient-to-br from-purple-400 to-pink-400 opacity-20 blur-2xl'/>
        <div className='animation-delay-3000 absolute right-[15%] top-[60%] h-40 w-40 animate-float rounded-full bg-gradient-to-br from-blue-400 to-purple-400 opacity-20 blur-2xl'/>
        <div className='animation-delay-5000 absolute left-[70%] top-[80%] h-36 w-36 animate-float rounded-full bg-gradient-to-br from-pink-400 to-orange-400 opacity-20 blur-2xl'/>
      </div>

      <style jsx global>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-3000 {
          animation-delay: 3s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-5000 {
          animation-delay: 5s;
        }
      `}</style>

      <Seo templateTitle={t('AI Talking Video Generator | Create Lip Sync Avatars')} description={t('Create realistic talking videos from any image or video with AI lip sync technology. Perfect for marketing, education & social media. Try now!')} image={`https://easy-peasy.ai/api/og-template?title=${t('AI Talking Video Generator')}`}/>

      <LandingPageNavigation />

      {/* Hero Section */}
      <div className='relative isolate overflow-hidden'>
        <div className='mx-auto max-w-7xl px-6 pb-24 pt-36 sm:pt-60 lg:px-8 lg:pt-32'>
          <div className='grid items-center gap-12 lg:grid-cols-2 lg:gap-16'>
            {/* Left — text */}
            <div className='text-center lg:text-left'>
              <div className='mb-8 inline-flex items-center rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 px-4 py-1.5 text-sm font-medium text-purple-700 backdrop-blur-sm ring-1 ring-purple-500/20 dark:text-purple-300 dark:ring-purple-500/30'>
                <SparklesIcon className='mr-2 h-4 w-4 animate-pulse'/>
                {t('Next-Gen AI Technology')}
              </div>
              <h1 className='text-4xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-6xl'>
                {t('Make Any Image or Video')}
                <span className='relative mt-2 mb-2 block'>
                  <span className='bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent '>
                    {t('Speak With AI')}
                  </span>
                </span>
              </h1>
              <p className='mt-8 text-lg leading-8 text-gray-600 dark:text-slate-300'>
                {t('Transform static visuals into dynamic talking videos. Upload an image or video, add your script or voice, and watch as our AI brings it to life with realistic lip-sync and facial expressions.')}
              </p>
              <div className='mt-10 flex flex-col items-center gap-4 sm:flex-row lg:justify-start'>
                <ButtonLink href='/ai-talking-videos' variant='dark' className='group relative overflow-hidden rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-3 text-base font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-105'>
                  <span className='relative z-10'>
                    {t('Create Talking Video')}
                  </span>
                  <div className='absolute inset-0 -z-10 bg-gradient-to-r from-purple-700 to-pink-700 opacity-0 transition-opacity group-hover:opacity-100'/>
                </ButtonLink>
                <a href='#examples' className='group inline-flex items-center gap-2 text-base font-semibold text-gray-700 transition-colors hover:text-purple-600 dark:text-slate-300 dark:hover:text-purple-400'>
                  {t('View Examples')}
                  <span className='inline-block transition-transform group-hover:translate-x-1' aria-hidden='true'>
                    →
                  </span>
                </a>
              </div>
            </div>

            {/* Right — two videos side by side */}
            <div className='flex justify-center gap-4 lg:justify-end'>
              {[
            {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b4cc830f-c5ca-435e-8da0-05a0049898aa.mp4',
                label: 'Person Speaking',
            },
            {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b5ef5f5c-cd31-42ef-864b-ddbc3a867a84.mp4',
                label: 'AI Avatar',
            },
        ].map((video) => (<div key={video.label} className='relative w-full max-w-[260px] overflow-hidden rounded-2xl border border-purple-200/40 shadow-2xl shadow-purple-500/10 dark:border-purple-800/30'>
                  <div className='relative aspect-[9/16] bg-gray-100 dark:bg-slate-800'>
                    <video autoPlay muted loop playsInline className='h-full w-full object-cover' src={video.src}/>
                  </div>
                  <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-3 pt-8'>
                    <span className='text-xs font-medium text-white/90'>
                      {video.label}
                    </span>
                  </div>
                </div>))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section aria-label='AI Talking Video Features' className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
        <div className='mx-auto max-w-2xl lg:text-center'>
          <h2 className='text-base font-semibold leading-7 text-purple-600 dark:text-purple-400'>
            {t('Powerful Features')}
          </h2>
          <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('Everything You Need for Professional Talking Videos')}
          </p>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
            {t('Our AI technology combines advanced voice synthesis with realistic facial animation to create authentic talking videos.')}
          </p>
        </div>
        <div className='mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none'>
          <dl className='grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3'>
            {features.map((feature) => (<div key={feature.name} className={`flex flex-col ${feature.highlight ? 'lg:border-l-2 lg:border-purple-600 lg:pl-6' : ''}`}>
                <dt className='flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900 dark:text-slate-200'>
                  {typeof feature.icon === 'string' ? (<span className='text-2xl'>{feature.icon}</span>) : (<feature.icon className='h-6 w-6 flex-none text-purple-600'/>)}
                  {t(feature.name)}
                </dt>
                <dd className='mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600 dark:text-slate-300'>
                  <p className='flex-auto'>{t(feature.description)}</p>
                </dd>
              </div>))}
          </dl>
        </div>
      </section>

      {/* Examples Section */}
      <section id='examples' aria-label='AI Talking Video Examples' className='bg-gray-50 dark:bg-slate-900/50 py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
              {t('See It In Action')}
            </h2>
            <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
              {t('Explore different use cases and see the quality of our AI-generated talking videos')}
            </p>
          </div>

          <div className='mt-16 space-y-8'>
            {/* Example Tabs */}
            <div className='flex justify-center space-x-4'>
              {examples.map((example, index) => (<button key={index} onClick={() => {
                setActiveExample(index);
                setIsExamplePlaying(false);
            }} className={`rounded-lg px-6 py-3 text-sm font-medium transition-colors ${activeExample === index
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}`}>
                  {t(example.title)}
                </button>))}
            </div>

            {/* Active Example */}
            <div className='mx-auto max-w-2xl'>
              <div className='overflow-hidden rounded-xl bg-white shadow-xl dark:bg-slate-800'>
                <div className='aspect-auto bg-gray-100 dark:bg-slate-900 relative'>
                  <video ref={exampleVideoRef} key={activeExample} loop playsInline controls className='h-full w-full  max-h-dvh' src={examples[activeExample].videoUrl} onPlay={() => setIsExamplePlaying(true)} onPause={() => setIsExamplePlaying(false)} onError={(e) => {
            console.error('Example video failed to load:', e);
        }}>
                    Your browser does not support the video tag.
                  </video>
                  {!isExamplePlaying && (<div className='absolute inset-0 flex items-center justify-center bg-black/20 cursor-pointer transition-opacity hover:bg-black/30' onClick={() => {
                if (exampleVideoRef.current) {
                    exampleVideoRef.current.play().catch(() => { });
                }
            }}>
                      <div className='bg-white/90 rounded-full p-6 shadow-lg transition-transform hover:scale-110'>
                        <PlayIcon className='h-12 w-12 text-purple-600'/>
                      </div>
                    </div>)}
                </div>
                <div className='p-6'>
                  <p className='text-sm text-gray-600 dark:text-slate-400'>
                    <span className='font-medium'>{t('Example')}: </span>
                    {t(examples[activeExample].prompt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('How It Works')}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
            {t('Create professional talking videos in three simple steps')}
          </p>
        </div>
        <div className='mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none'>
          <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
            <div className='relative'>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-purple-600 text-white'>
                1
              </div>
              <div className='mt-4'>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-slate-200'>
                  {t('Upload Your Visual')}
                </h3>
                <p className='mt-2 text-base text-gray-600 dark:text-slate-400'>
                  {t('Upload a portrait image or video that you want to bring to life')}
                </p>
              </div>
            </div>
            <div className='relative'>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-purple-600 text-white'>
                2
              </div>
              <div className='mt-4'>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-slate-200'>
                  {t('Add Your Script')}
                </h3>
                <p className='mt-2 text-base text-gray-600 dark:text-slate-400'>
                  {t('Enter your text and choose a voice, or upload your own audio')}
                </p>
              </div>
            </div>
            <div className='relative'>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-purple-600 text-white'>
                3
              </div>
              <div className='mt-4'>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-slate-200'>
                  {t('Generate & Download')}
                </h3>
                <p className='mt-2 text-base text-gray-600 dark:text-slate-400'>
                  {t('Our AI creates a realistic talking video with perfect lip-sync')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Actors Showcase Section */}
      <section className='relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-slate-900 dark:via-purple-900/10 dark:to-slate-900 py-24 sm:py-32'>
        {/* Decorative background elements */}
        <div className='absolute inset-0 -z-10'>
          <div className='absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-purple-200 opacity-20 blur-3xl dark:bg-purple-700 dark:opacity-10'/>
          <div className='absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-pink-200 opacity-20 blur-3xl dark:bg-pink-700 dark:opacity-10'/>
        </div>

        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          {/* Section Header */}
          <div className='mx-auto max-w-2xl text-center mb-16'>
            <div className='mb-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 px-4 py-1.5 text-sm font-medium text-purple-700 backdrop-blur-sm ring-1 ring-purple-500/20 dark:text-purple-300 dark:ring-purple-500/30'>
              <UserIcon className='h-4 w-4'/>
              100+ AI Actors
            </div>
            <h2 className='text-4xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-5xl'>
              Meet Your{' '}
              <span className='bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent'>
                AI Cast
              </span>
            </h2>
            <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
              Choose from our diverse collection of AI actors, each with unique
              voices and appearances. From professional presenters to casual
              creators, find the perfect face for your content.
            </p>
          </div>

          {/* Category Filter Pills */}
          <div className='mb-12 flex flex-wrap justify-center gap-3'>
            {categories.map((category) => {
            const isActive = selectedCategory === category;
            return (<button key={category} onClick={() => setSelectedCategory(category)} className={isActive
                    ? 'group relative overflow-hidden rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30'
                    : 'group relative overflow-hidden rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 bg-white text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}>
                  <span className='relative z-10 capitalize'>{category}</span>
                  {isActive && (<div className='absolute inset-0 -z-10 bg-gradient-to-r from-purple-700 to-pink-700 opacity-0 transition-opacity group-hover:opacity-100'/>)}
                </button>);
        })}
          </div>

          {/* Actors Grid with Cinematic Cards */}
          <div className='grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'>
            {filteredActors.map((actor, index) => {
            const animationDelay = index * 0.05;
            return (<div key={actor.id} className='group relative' style={{
                    animation: 'fadeInUp 0.6s ease-out both',
                    animationDelay: animationDelay + 's',
                }}>
                  {/* Card */}
                  <div className='relative overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-gray-200/50 transition-all duration-500 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 dark:bg-slate-800 dark:ring-slate-700/50'>
                    {/* Actor Image with Spotlight Effect */}
                    <div className='relative aspect-[3/4] overflow-hidden'>
                      <Image src={actor.photo} alt={actor.name} width={300} height={400} className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-110'/>

                      {/* Gradient Overlay - Creates spotlight effect */}
                      <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 transition-opacity duration-500 group-hover:opacity-40'/>

                      {/* Animated Shine Effect on Hover */}
                      <div className='absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 group-hover:translate-x-[100%]'/>
                    </div>

                    {/* Actor Info Overlay */}
                    <div className='absolute bottom-0 left-0 right-0 p-4 transition-all duration-300 group-hover:pb-6'>
                      <h3 className='text-sm font-bold text-white mb-1 drop-shadow-lg'>
                        {actor.name}
                      </h3>
                      {actor.description && (<p className='text-xs text-white/90 line-clamp-2 opacity-0 max-h-0 transition-all duration-300 group-hover:opacity-100 group-hover:max-h-20 drop-shadow'>
                          {actor.description}
                        </p>)}
                    </div>

                    {/* Category Badge */}
                    <div className='absolute top-3 right-3 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-purple-700 backdrop-blur-sm opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:bg-slate-900/90 dark:text-purple-300'>
                      {actor.category}
                    </div>

                    {/* Decorative Corner Accent */}
                    <div className='absolute top-0 left-0 h-20 w-20 bg-gradient-to-br from-purple-500/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100'/>
                  </div>
                </div>);
        })}
          </div>

          {/* Call to Action */}
          <div className='mt-16 text-center'>
            <ButtonLink href='/ai-talking-videos' variant='dark' className='group relative overflow-hidden rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-4 text-base font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-105'>
              <span className='relative z-10 flex items-center gap-2'>
                Try AI Actors Now
                <SparklesIcon className='h-5 w-5 transition-transform group-hover:rotate-12'/>
              </span>
              <div className='absolute inset-0 -z-10 bg-gradient-to-r from-purple-700 to-pink-700 opacity-0 transition-opacity group-hover:opacity-100'/>
            </ButtonLink>
            <p className='mt-4 text-sm text-gray-600 dark:text-slate-400'>
              All actors include voice synthesis • Perfect lip-sync • HD quality
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <div className='bg-gray-50 dark:bg-slate-900/50 py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
              {t('Unlimited Possibilities')}
            </h2>
            <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
              {t('From marketing to education, our AI talking videos serve countless industries')}
            </p>
          </div>
          <div className='mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3'>
            {useCases.map((useCase) => (<div key={useCase.title} className='flex flex-col overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-gray-200/50 dark:bg-slate-800 dark:ring-slate-700'>
                <div className='flex flex-1 flex-col p-8'>
                  <div className='mb-4 text-4xl'>{useCase.icon}</div>
                  <h3 className='text-xl font-semibold text-gray-900 dark:text-slate-200'>
                    {t(useCase.title)}
                  </h3>
                  <p className='mt-3 flex-1 text-base text-gray-600 dark:text-slate-400'>
                    {t(useCase.description)}
                  </p>
                </div>
              </div>))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
            {t('Frequently Asked Questions')}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
            {t('Everything you need to know about AI talking video generation')}
          </p>
        </div>
        <div className='mx-auto mt-16 max-w-2xl'>
          <dl className='space-y-8'>
            {faqItems.map((faq, index) => (<div key={index} className='pt-8 border-t border-gray-200 dark:border-slate-700'>
                <dt className='text-lg font-semibold text-gray-900 dark:text-slate-200'>
                  {t(faq.question)}
                </dt>
                <dd className='mt-2 text-base text-gray-600 dark:text-slate-400'>
                  {t(faq.answer)}
                </dd>
              </div>))}
          </dl>
        </div>
      </div>

      {/* Pricing CTA Section */}
      <div className='bg-purple-700 dark:bg-purple-900'>
        <div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl'>
              {t('Ready to Create Amazing Talking Videos?')}
            </h2>
            <p className='mx-auto mt-6 max-w-xl text-lg leading-8 text-purple-100'>
              {t('Join thousands of creators using AI to bring their visuals to life')}
            </p>
            <div className='mt-10 flex items-center justify-center gap-x-6'>
              <ButtonLink href='/ai-talking-videos' variant='light' className='rounded-full bg-white text-purple-700 hover:bg-gray-100 px-8 py-3 text-base font-semibold'>
                {t('Start Creating Now')}
              </ButtonLink>
            </div>
            <ul className='mt-8 grid grid-cols-1 gap-4 text-left text-sm leading-6 text-purple-100 sm:grid-cols-2 sm:gap-6'>
              {pricingFeatures.map((feature) => (<li key={feature} className='flex gap-x-3'>
                  <CheckIcon className='h-6 w-5 flex-none text-white' aria-hidden='true'/>
                  {t(feature)}
                </li>))}
            </ul>
          </div>
        </div>
      </div>

      {/* Related AI Video Tools */}
      <section className='py-20 sm:py-28'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
              Explore More AI Video Tools
            </h2>
            <p className='mt-4 text-lg text-gray-600 dark:text-slate-300'>
              Discover specialized tools for every video creation need
            </p>
          </div>
          <div className='mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
            {[
            {
                href: '/ai-video-generator/talking-head',
                title: 'AI Talking Head Videos',
                description: 'Create professional talking head videos for YouTube, courses, and news content.',
            },
            {
                href: '/ai-video-generator/spokesperson',
                title: 'AI Spokesperson',
                description: 'Generate brand-consistent spokesperson videos 50x cheaper than hiring actors.',
            },
            {
                href: '/ai-video-generator/make-image-talk',
                title: 'Make Image Talk',
                description: 'Turn any photo into a talking video — portraits, illustrations, and AI images.',
            },
            {
                href: '/ai-video-generator/talking-avatar',
                title: 'AI Talking Avatar',
                description: 'Build a consistent digital persona with 100+ pre-built avatars.',
            },
        ].map((tool) => (<Link key={tool.href} href={tool.href} className='group rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800'>
                <h3 className='text-lg font-semibold text-gray-900 group-hover:text-purple-600 dark:text-white dark:group-hover:text-purple-400'>
                  {tool.title}
                </h3>
                <p className='mt-2 text-sm text-gray-600 dark:text-slate-400'>
                  {tool.description}
                </p>
                <span className='mt-4 inline-flex items-center gap-1 text-sm font-medium text-purple-600 dark:text-purple-400'>
                  Learn more
                  <span className='transition-transform group-hover:translate-x-1'>
                    →
                  </span>
                </span>
              </Link>))}
          </div>
        </div>
      </section>

      <LogosCloud />
      <FeaturedLogos />
      <Testimonials />
      <CallToAction preset='ai-talking-videos'/>
      <Footer />
    </main>);
};
export default TalkingVideosLanding;

    async function __Next_Translate__getStaticProps__1a12e86edb1__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/talking',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86edb1__ as getStaticProps }
  